import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  getArticlesByName,
  getArticlesFiltered,
  getBranchOfficesFiltered,
  getBrandsFiltered,
  getCategoriesFiltered,
  getArticleGroups,
} from "../../api/Services";
import { hasPermission, privilegesEnum } from "../../helpers/PermissionHelper";
import { getOrganizationId } from "../../api/Helper";

import {
  Table,
  Button,
  Modal,
  Select,
  message,
  Spin,
  DatePicker,
} from "antd";
import {
  CalendarOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";

import styles from "./Pricings.module.scss";
import hardcodedData from "./hardcodedData.json";

const { RangePicker } = DatePicker;



const columns = [
  {
    title: "Artículo",
    dataIndex: "name",
    key: "name",
    width: 40,
    render: (text: string, record: any) => (
      <a href={`/pricings/articles/${record.key}/detail`} style={{ color: "#1890ff" }}>{text}</a>
    ),
  },
  { title: "Costo", dataIndex: "cost", key: "cost", width: 20 },
  { 
    title: "Precio actual", 
    dataIndex: "currentPrice", 
    key: "currentPrice", 
    width: 20,
    render: (price: any, record: any) => (
      <a href={`/pricings/create?articleId=${record.key}`} style={{ color: "#1890ff" }}>
        {price ? `$${price.toFixed(2)}` : '-'}
      </a>
    ),
  },
  { title: "Margen actual", dataIndex: "currentMargin", key: "currentMargin", width: 20 },
  { title: <>Fecha de fin<br/>del precio</>, dataIndex: "endDate", key: "endDate", width: 25 },
  { title: <>Precio sugerido<br/>por sistema</>, dataIndex: "suggestedPrice", key: "suggestedPrice", width: 25 },
  { title: <>Margen sugerido<br/>por sistema</>, dataIndex: "suggestedMargin", key: "suggestedMargin", width: 25 },
  { title: <>Precio futuro</>, dataIndex: "futurePrice", key: "futurePrice", width: 20 },
  { title: <>Margen futuro</>, dataIndex: "futureMargin", key: "futureMargin", width: 20 },
  { 
    title: <>Fecha<br/>futura</>, 
    dataIndex: "futureDate", 
    key: "futureDate", 
    width: 20, 
    className: "narrow-col",
    onCell: () => ({
      style: { width: 20, minWidth: 20, maxWidth: 20, paddingLeft: 8, paddingRight: 0, textAlign: "left" as "left" }
    }),
    onHeaderCell: () => ({
      style: { width: 20, minWidth: 20, maxWidth: 20, paddingLeft: 8, paddingRight: 0, textAlign: "left" as "left" }
    })
  },
  { 
    title: <>Precio<br/>promocional</>, 
    dataIndex: "promotionalPrice", 
    key: "promotionalPrice", 
    width: 30, 
    className: "narrow-col",
    onCell: () => ({
      style: { width: 30, minWidth: 30, maxWidth: 30, paddingLeft: 8, paddingRight: 0, textAlign: "left" as "left" }
    }),
    onHeaderCell: () => ({
      style: { width: 30, minWidth: 30, maxWidth: 30, paddingLeft: 8, paddingRight: 0, textAlign: "left" as "left" }
    })
  },
  { 
    title: <>Margen<br/>promocional</>, 
    dataIndex: "promotionalMargin", 
    key: "promotionalMargin", 
    width: 30, 
    className: "narrow-col",
    onCell: () => ({
      style: { width: 30, minWidth: 30, maxWidth: 30, paddingLeft: 8, paddingRight: 0, textAlign: "left" as "left" }
    }),
    onHeaderCell: () => ({
      style: { width: 30, minWidth: 30, maxWidth: 30, paddingLeft: 8, paddingRight: 0, textAlign: "left" as "left" }
    })
  },
];

const Pricings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [data, setData] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [brands, setBrands] = useState<any[]>([]);
  const [branchOffices, setBranchOffices] = useState<any[]>([]);
  const [articleGroups, setArticleGroups] = useState<any[]>([]);
  const [branchOfficeFilterValue, setBranchOfficeFilterValue] = useState<number>();
  const [loading, setLoading] = useState(false);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [totalSize, setTotalSize] = useState(0);
  const [actualPage, setActualPage] = useState(1);
  const [categoryIdFilterValue, setCategoryIdFilterValue] = useState<number>();
  const [brandIdFilterValue, setBrandIdFilterValue] = useState<number>();
  const [articleGroupIdFilterValue, setArticleGroupIdFilterValue] = useState<number>();
  const [articleOptions, setArticleOptions] = useState<any[]>([]);
  const [articleIdFilterValue, setArticleIdFilterValue] = useState("");
  const [dateRangeFilterValue, setDateRangeFilterValue] = useState<any>(null);
  const [isOrg5, setIsOrg5] = useState(false);

  useEffect(() => {
    const fetchOrg = async () => {
      const orgId = await getOrganizationId();
      setIsOrg5(parseInt(orgId) === 5);
    };
    fetchOrg();
  }, []);

  useEffect(() => {
    if (hasPermission(privilegesEnum.READ_PRICINGS) && !isOrg5) {
      handleFetchArticles();
    }
  }, [
    categoryIdFilterValue,
    brandIdFilterValue,
    branchOfficeFilterValue,
    articleIdFilterValue,
    articleGroupIdFilterValue,
    dateRangeFilterValue,
    isOrg5
  ]);

  useEffect(() => {
    if (hasPermission(privilegesEnum.READ_PRICINGS)) {
      handleSearchBrands("");
      handleSearchCategories("");
      handleSearchBranchOffices("");
      handleSearchArticleGroups("");
    }
  }, []);

  const handleSearchArticles = async (value: string) => {
    if (value.length > 2) {
      setLoadingArticles(true);
      const response = await getArticlesByName(value);
      const options = response.articles.map((item: any) => ({
        value: item.id,
        text: item.name,
      }));
      setArticleOptions(options);
      setLoadingArticles(false);
    } else setArticleOptions([]);
  };

  const handleSearchBranchOffices = async (value: string) => {
    const response = await getBranchOfficesFiltered(0, {
      name: value,
    });
    setBranchOffices(response.branchOffices);
  };

  const handleSearchCategories = async (value: string) => {
    const response = await getCategoriesFiltered(value);
    setCategories(response);
  };

  const handleSearchBrands = async (value: string) => {
    const response = await getBrandsFiltered(value);
    setBrands(response);
  };

  const handleSearchArticleGroups = async (value: string) => {
    const response = await getArticleGroups(1, { name: value });
    setArticleGroups(response.articleGroups);
  };

  const handleFetchArticles = async (pagination = actualPage) => {
    setLoading(true);
    const response = await getArticlesFiltered(
      pagination - 1,
      {
        categoryId: categoryIdFilterValue?.toString(),
        brandId: brandIdFilterValue,
        articleName: articleIdFilterValue,
      },
      false,
      "articleProjection"
    );

    const articlesWithPricingInfo = response.articles.map((article: any) => {
      const suggestedPrice = article.cost ? article.cost * 1.6 : 0;
      const currentMargin = article.defaultPrice && article.cost
        ? ((article.defaultPrice - article.cost) / article.defaultPrice * 100).toFixed(2)
        : 0;
      const suggestedMargin = suggestedPrice && article.cost
        ? ((suggestedPrice - article.cost) / suggestedPrice * 100).toFixed(2)
        : 0;

      const actualPrices = article.actualPrices || [];
      const currentPrice = actualPrices.length > 0 ? actualPrices[0] : null;
      
      const futurePrice = article.prices?.find((price: any) => 
        new Date(price.date) > new Date()
      );

      const futureMargin = futurePrice && article.cost
        ? ((futurePrice.price - article.cost) / futurePrice.price * 100).toFixed(2)
        : 0;

      const promotionalPrice = null;
      const promotionalMargin = 0;

      return {
        ...article,
        suggestedPrice: suggestedPrice.toFixed(2),
        currentMargin,
        suggestedMargin,
        futurePrice,
        futureMargin,
        promotionalPrice,
        promotionalMargin,
        currentPrice,
      };
    });

    setData(articlesWithPricingInfo);
    setTotalSize(response.totalElements);
    setLoading(false);
  };

  const getOptions = (items: any) => {
    const options: any[] = [];

    items?.forEach(function (item: any) {
      options.push(
        <Select.Option value={item.id} key={item.id}>
          {item.name}
        </Select.Option>
      );
    });

    return options;
  };

  const getArticleGroupOptions = (items: any) => {
    const options: any[] = [];

    items?.forEach(function (item: any) {
      options.push(
        <Select.Option value={item.id} key={item.id}>
          {item.name}
        </Select.Option>
      );
    });

    return options;
  };

  const handlePagination = (page: number) => {
    handleFetchArticles(page);
    setActualPage(page);
  };

  const showDeleteConfirm = (itemId: number) => {
    Modal.confirm({
      title: t("Common-DeleteConfirm"),
      icon: <ExclamationCircleOutlined />,
      content: "",
      okText: t("Common-Yes"),
      okType: "danger",
      cancelText: t("Common-No"),
      onOk() {
        handleDelete(itemId);
      },
    });
  };

  const handleDelete = async (articleId: number) => {
    setLoading(true);
    message.success(t("Common-DeleteSuccessMessage"));
    await handleFetchArticles();
    setLoading(false);
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t("Common-PricingsTitle")}</h1>
        <div className={styles.actionButtonsWrapper}>
          <Button
            onClick={() => navigate("/pricings/calendar")}
            icon={<CalendarOutlined />}
          ></Button>
          <Button
            href="/pricings/create"
            className={styles.newButton}
            type="primary"
            icon={<PlusOutlined />}
          />
        </div>
      </div>
      <div className={styles.contentWrapper}>
        <div className={styles.tableWrapper}>
          <div className={styles.headerTableWrapper}>
            <div className={styles.filtersContainer}>
              <div className={styles.filtersSection}>
                <div className={styles.filtersGrid}>
                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>{t("Common-Category")}</label>
                    <Select
                      allowClear
                      showSearch
                      className={styles.filterSelect}
                      onSearch={handleSearchCategories}
                      placeholder={t("Common-SearchByCategory")}
                      filterOption={(input: string, option: any) =>
                        option.children
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                      onSelect={(_, option) => {
                        if (option) setCategoryIdFilterValue(option.value);
                      }}
                      onClear={() => setCategoryIdFilterValue(undefined)}
                    >
                      {getOptions(categories)}
                    </Select>
                  </div>
                  
                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>{t("Common-Brand")}</label>
                    <Select
                      allowClear
                      showSearch
                      className={styles.filterSelect}
                      onSearch={handleSearchBrands}
                      placeholder={t("Common-SearchByBrand")}
                      filterOption={(input: string, option: any) =>
                        option.children
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                      onSelect={(_, option) => {
                        if (option) setBrandIdFilterValue(option.value);
                      }}
                      onClear={() => setBrandIdFilterValue(undefined)}
                    >
                      {getOptions(brands)}
                    </Select>
                  </div>
                  
                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>{t("Common-BranchOffice")}</label>
                    <Select
                      allowClear
                      className={styles.filterSelect}
                      showSearch
                      onSearch={handleSearchBranchOffices}
                      placeholder={t("Common-SearchByBranchOffice")}
                      filterOption={(input: string, option: any) =>
                        option.children
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                      onSelect={(_, option) => {
                        if (option) setBranchOfficeFilterValue(option.value);
                      }}
                      onClear={() => setBranchOfficeFilterValue(undefined)}
                    >
                      {getOptions(branchOffices)}
                    </Select>
                  </div>
                  
                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>{t("Common-Date")} {t("Common-Range")}</label>
                    <RangePicker
                      className={styles.filterSelect}
                      placeholder={[t("Common-StartDate"), t("Common-EndDate")]}
                      onChange={(dates) => setDateRangeFilterValue(dates)}
                    />
                  </div>
                  
                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>{t("Common-Article")}</label>
                    <Select
                      showSearch
                      placeholder={t("Common-SearchByArticle")}
                      allowClear
                      onClear={() => {
                        setArticleIdFilterValue("");
                      }}
                      onSelect={(_, option) => {
                        if (option) {
                          setArticleIdFilterValue(option.value);
                        }
                      }}
                      notFoundContent={
                        loadingArticles ? <Spin size="small" /> : null
                      }
                      className={styles.filterSelect}
                      defaultActiveFirstOption={false}
                      showArrow={false}
                      filterOption={false}
                      onSearch={handleSearchArticles}
                      options={(articleOptions || []).map((d: any) => ({
                        value: d.value,
                        label: d.text,
                      }))}
                    />
                  </div>
                  
                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>{t("Common-ArticleGroup")}</label>
                    <Select
                      allowClear
                      className={styles.filterSelect}
                      showSearch
                      onSearch={handleSearchArticleGroups}
                      placeholder={t("Common-SearchByArticleGroup")}
                      filterOption={(input: string, option: any) =>
                        option.children
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                      onSelect={(_, option) => {
                        if (option) setArticleGroupIdFilterValue(option.value);
                      }}
                      onClear={() => setArticleGroupIdFilterValue(undefined)}
                    >
                      {getArticleGroupOptions(articleGroups)}
                    </Select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Table
            size="small"
            className={styles.table}
            rowKey={(record) => record.key}
            loading={loading}
            columns={columns}
            dataSource={isOrg5 ? hardcodedData.pricings : data}
            pagination={false}
            tableLayout="fixed"
          />
        </div>
      </div>
    </div>
  );
};

export default Pricings;
